import * as moment from 'moment-timezone';

export function plainText(content, keep_line_breaks = false) {
  if (keep_line_breaks) {
    return content.replace(/<(?!br\s*\/?)[^>]+>/g, '');
  }

  return content.replace(/<[^>]*>/g, '');
}

export function formatMoney(number, decPlaces?, decSep?, thouSep?) {
  decPlaces = isNaN(decPlaces = Math.abs(decPlaces)) ? 2 : decPlaces;
  decSep = typeof decSep === 'undefined' ? '.' : decSep;
  thouSep = typeof thouSep === 'undefined' ? ',' : thouSep;
  const sign = number < 0 ? '-' : '';
  const intVal = parseInt(number = Math.abs(Number(number) || 0).toFixed(decPlaces));
  const i = String(intVal);
  const j = (i.length) > 3 ? i.length % 3 : 0;

  return sign +
    (j ? i.substr(0, j) + thouSep : '') +
    i.substr(j).replace(/(\d{3})(?=\d)/g, '$1' + thouSep) +
    (decPlaces ? decSep + Math.abs(number - intVal).toFixed(decPlaces).slice(2) : '');
}

export function getDatePart(part) {
  return ('0' + part.toString()).slice(-2)
}

export function parseISODateString(dateStr: string) {
  //dateStr is in YYYY-MM-DD format
  let ds = dateStr.split('-');

  return new Date(parseInt(ds[0]), parseInt(ds[1]) - 1, parseInt(ds[2]), 0, 0, 0, 0);
}

export function formatDateForQuery(date: Date, includeTime: boolean = false) {
  //query expects a date in UTC YYYY-MM-DD HH:MM:SS format
  let result =  date.getUTCFullYear().toString() +
    '-' + getDatePart(date.getUTCMonth() + 1) +
    '-' + getDatePart(date.getUTCDate())

  if (includeTime) {
    result += ' ' + getDatePart(date.getUTCHours()) +
      ':' + getDatePart(date.getUTCMinutes()) +
      ':' + getDatePart(date.getUTCSeconds())
  }

  return result;
}

export function getOrdinal(n: number) {
  const s = ["th","st","nd","rd"];
  const v = n % 100;

  return n.toString() + (s[(v-20)%10] || s[v] || s[0]);
}

export function tinyMCETTextOnly(editor) {
  //if there's a root wrapper element, remove it and replace all line break html elements with text line breaks
  const body = editor.getBody();

  if (body.children.length === 1) {
    const root = body.children[0];

    if (root.tagName.toLowerCase() === 'div') {
      body.style.cssText = root.style.cssText;
      body.removeChild(root);
      body.innerHTML = root.innerHTML;
    }
  }
}

export function tinyMCESingleLine(editor) {
  editor.on('keydown', function (event) {
    if (event.keyCode == 13)  {
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
  });
}

export function formatISODate(timestamp) {
  const dt = new Date(timestamp);

  return (dt.getMonth() + 1).toString() + '/' + dt.getDate().toString() + '/' + dt.getFullYear();
}

export function formatISOTimestamp(timestamp, includeSeconds = true) {
  const dt = new Date(timestamp);
  const am_pm = dt.getHours() >= 12 ? 'PM' : 'AM';
  const hours = (dt.getHours() === 0) ? 12 : (dt.getHours() > 12) ? dt.getHours() - 12 : dt.getHours();
  const seconds = includeSeconds ? ':' + ('0' + dt.getSeconds().toString()).slice(-2) : '';

  return (dt.getMonth() + 1).toString() + '/' + dt.getDate().toString() + ' ' + hours.toString() + ':' +
    ('0' + dt.getMinutes().toString()).slice(-2) + seconds + ' ' + am_pm;
}

export function timezoneAwareToUtc(timestamp, timezone) {
  // When timestamp is a string (e.g., "2024-03-18 00:00:00"), interpret it
  // as being in the specified timezone. When it's a number (Unix timestamp),
  // it's already in UTC, so the timezone just affects display.
  if (typeof timestamp === 'string') {
    return moment.tz(timestamp, 'YYYY-MM-DD HH:mm:ss', timezone).utc();
  }
  return moment.tz(timestamp, timezone).utc();
}

export function formatTime(input) {
  if (!input) return '';
  const date = new Date(input);
  return date.toLocaleTimeString();
}

export const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

export const isDefined = (value: any): boolean => {
  return value !== null && value !== undefined;
};

export const isObjectEmpty = (obj: any): boolean => {
  return Object.keys(obj).length === 0;
};

export const downloadFromUrl = (url: string) => {
  let dwldLink = document.createElement("a");
  dwldLink.setAttribute("href", url);
  dwldLink.style.visibility = "hidden";
  document.body.appendChild(dwldLink);
  dwldLink.click();
  document.body.removeChild(dwldLink);
}

export function copyToClipboard(data: string) {
  const el = document.createElement('textarea');
  el.value = data;
  document.body.appendChild(el);
  el.select();
  document.execCommand('copy');
  document.body.removeChild(el);
}

export function pad(num) {
  return ('0' + num).slice(-2);
}

export function convertSecondsToSessionTime(secs) {
  if (secs) {
    let minutes = Math.floor(secs / 60);
    secs = secs % 60;
    let hours = Math.floor(minutes / 60);
    minutes = minutes % 60;
    return `${pad(hours)}:${pad(minutes)}:${pad(Math.floor(secs))}`;
  }

  return null
}

export function fixMouseflowRecordingUrl(url: string) {
  if (url) {
    if (url.startsWith('api-')) {
      url = url.replace('api-', 'https://');
    } else {
      url = url.replace('app', 'us');
    }
  }

  return url;
}

export function formatCallCenterTime(timeStamp: string, isEndTime: boolean) {
  if (isEndTime && (timeStamp === '12:00 am')) {
    return '23:59:59'; // convert midnight end time to the last second of the day
  }

  let parts = timeStamp.split(' ');
  let meridian = parts[1];
  let timeParts = parts[0].split(':');
  let hourString = (timeParts[0][0] === '0') ? timeParts[0].substr(1) : timeParts[0];
  let hour = parseInt(hourString, 10);

  // convert 12 hour HH:MM am/pm timestamp to 24 hour HH:MM:SS format
  if (meridian === 'am') { // am
    if (hour === 12) {
      hourString = '00';
    } else if (hour < 10) {
      hourString = '0' + hour.toString();
    } else {
      hourString = hour.toString();
    }
  } else { // pm
    if (hour < 12) {
      hour += 12;
    }

    hourString = hour.toString();
  }

  return hourString + ':' + timeParts[1] + ':00';
}

export function parseTime(timeString: string) {
  let parts = timeString.split(':');

  // convert end of day timestamp to midnight
  if ((parts[0] === '23') && (parts[1] === '59') && (parts[2] === '59')) {
    return '12:00 am';
  }

  // convert 24 hour timestamp to 12 hour HH:MM am/pm format
  let hour = parseInt(parts[0], 10);
  let meridian = 'am';
  let pad = '';

  if (hour === 0) {
    // 0 hour is 12 am in midnight
    hour = 12;
    meridian = 'am';
  } else if (hour > 12) {
    hour -= 12;
    meridian = 'pm';
  } else if (hour === 12) {
    // 12 hour is 12 in after noon, that is pm
    meridian = 'pm'
  }

  if (hour < 10) {
    pad = '0';
  }

  return pad + hour.toString() + ':' + parts[1] + ' ' + meridian;
}

export function createBaseUrl(host: string) {
  const scheme = host.split(':')[0].endsWith('localhost') ? 'http' : 'https';
  return `${scheme}://${host}/`;
}

export function pluralize(count: number, unit: string): string {
  return `${count} ${unit}${count === 1 ? '' : 's'}`;
}

export function formatBillingIntervalDays(value: any): string {
  let result = '--';
  const n = Number(value);
  if (n === 0) {
    result = 'Unknown';
  } else if (n % 365 === 0) {
    result = pluralize(n / 365, 'Year');
  } else if (n % 30 === 0) {
    result = pluralize(n / 30, 'Month');
  } else if (n % 7 === 0) {
    result = pluralize(n / 7, 'Week');
  } else {
    result = pluralize(n, 'Day');
  }
  return result;
}

